import { trace } from '@opentelemetry/api';
import { EnvParams, isProductionEnvironment } from '@zorro/environment';
import pino from 'pino';

export const defaultLogLevel = 'info';

export const getLogLevel = (): string =>
  process.env[EnvParams.PINO_LOG_LEVEL] || defaultLogLevel;

/*
 * Reads the ambient logging context. On the Next.js server it is seeded
 * per-request by @zorro/web/request-logging (instrumentation.ts); a browser
 * implementation could publish a singleton behind the same getStore()
 * interface. Accessed through a global symbol rather than an import so this
 * file stays safe to load on the client / edge and in apps that don't
 * register request logging — there it's simply absent.
 */
const REQUEST_CONTEXT_GLOBAL_KEY = Symbol.for('zorro.requestContext');

interface RequestContext {
  correlationId?: string;
  // Monolith customProps structure; roles/permissions are ';'-joined
  userId?: string;
  roles?: string;
  permissions?: string;
  tenantId?: string;
}

/*
 * The runtime-agnostic contract behind the global symbol. On the Next.js
 * server it is fulfilled by an AsyncLocalStorage (request-scoped); a browser
 * implementation could fulfil it with a singleton store.
 */
interface RequestContextStorage {
  getStore(): RequestContext | undefined;
}

function getRequestContext(): RequestContext | undefined {
  const storage = (globalThis as Record<symbol, unknown>)[
    REQUEST_CONTEXT_GLOBAL_KEY
  ] as RequestContextStorage | undefined;
  return storage?.getStore();
}

const config: pino.LoggerOptions = {
  level: getLogLevel(),
  /*
   * Stamps every log line written during a request (e.g. inside
   * getServerSideProps or API handlers) with the same correlationId and
   * trace ids as the request's access-log line, so applicative logs and
   * request logs are joinable — mirroring the monolith's customProps.
   */
  mixin: () => {
    try {
      const requestContext = getRequestContext();
      const spanContext = trace.getActiveSpan()?.spanContext();
      return {
        ...(requestContext?.correlationId && {
          correlationId: requestContext.correlationId,
        }),
        ...(requestContext?.userId && { userId: requestContext.userId }),
        ...(requestContext?.roles && { roles: requestContext.roles }),
        ...(requestContext?.permissions && {
          permissions: requestContext.permissions,
        }),
        ...(requestContext?.tenantId && { tenantId: requestContext.tenantId }),
        ...(spanContext && {
          trace_id: spanContext.traceId,
          span_id: spanContext.spanId,
        }),
      };
    } catch {
      return {};
    }
  },
};

if (!isProductionEnvironment()) {
  config.transport =
    /*
     * INFO: We encounter open handles here with unit test runs,
     * therefore we enable pretty printing ONLY for "development" env
     * and have it disabled for both "production" and "test" envs (set by jest)
     *
     * See more: https://github.com/pinojs/pino/issues/1268
     */
    process.env.NODE_ENV === 'development'
      ? { target: 'pino-pretty' }
      : undefined;
}

export const logger = pino(config);

// The user-identity slice of the request context, exactly as stored
export type RequestUserContext = Omit<RequestContext, 'correlationId'>;

/*
 * Stamps the user identity onto the request context (when request logging is
 * registered); the pino mixin then adds it to every log line written during
 * this request — including the access-log line — in the monolith's
 * customProps structure (roles/permissions ';'-joined). The async context is
 * the single identity mechanism: no child-logger bindings.
 *
 * Call once per request after authentication resolves (see the session
 * wrappers in @zorro/web/shared-ui). Vendor-agnostic on purpose: the caller
 * owns the mapping from its auth provider's session to this shape.
 *
 * No-ops when the request context is absent (client, edge, `next dev`,
 * non-instrumented apps) — request logging is production-topology only.
 */
export function setRequestUserContext(user: RequestUserContext): void {
  try {
    const requestContext = getRequestContext();
    if (requestContext) {
      Object.assign(requestContext, user);
    }
  } catch {
    // never let logging context-stamping break the request
  }
}

// Redaction constants and utilities moved to @zorro/shared/redaction
// (re-exported from this lib's barrel for backwards compatibility)
